import { useState, useEffect, useCallback } from 'react';

import { getSWRegistration, onUpdateFound } from '@/lib/pwa/registerSW';

interface UseUpdateAvailableReturn {
  updateAvailable: boolean;
  updateServiceWorker: () => void;
}

export function useUpdateAvailable(): UseUpdateAvailableReturn {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const unsubscribe = onUpdateFound(() => setUpdateAvailable(true));

    const hasSW =
      typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

    let refreshing = false;
    // Página que nasceu SEM controller vai receber um controllerchange que não
    // é update: é o claim inicial do SW recém-instalado (primeira visita ao
    // app neste perfil). Recarregar aí é um flash gratuito segundos depois de
    // o usuário chegar — e pode abortar uma navegação em curso. Só a troca de
    // controller de uma página JÁ controlada significa update de verdade.
    let wasControlled = hasSW && !!navigator.serviceWorker.controller;
    const onControllerChange = () => {
      if (!wasControlled) {
        wasControlled = true;
        return;
      }
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    };
    if (hasSW) {
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    }

    return () => {
      unsubscribe();
      if (hasSW) {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      }
    };
  }, []);

  const updateServiceWorker = useCallback(() => {
    getSWRegistration().then((reg) => {
      reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    });
  }, []);

  return {
    updateAvailable,
    updateServiceWorker,
  };
}
